import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ParticleBackground from '../../components/feature/ParticleBackground';
import ScrollProgress from '../../components/feature/ScrollProgress';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { useCountUp } from '../../hooks/useCountUp';

function AnimatedCounter({
  value,
  prefix = '',
  suffix = '',
}: {
  value: number;
  prefix?: string;
  suffix?: string;
}) {
  const { count, ref } = useCountUp(value, 2000);
  return (
    <div ref={ref}>
      {prefix}
      {count.toLocaleString()}
      {suffix}
    </div>
  );
}

function TypingText({
  text,
  className = '',
}: {
  text: string;
  className?: string;
}) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setStarted(true);
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(interval);
        setDone(true);
      }
    }, 80);
    return () => clearInterval(interval);
  }, [started, text]);

  return (
    <span ref={ref} className={className}>
      {displayed}
      {!done && started && <span className="typing-cursor" />}
    </span>
  );
}

export default function HomePage() {
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    phone: '',
    lineId: '',
    socialLinks: '',
    gameType: '',
    experience: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [headerSolid, setHeaderSolid] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const scrollRef = useScrollReveal();

  // Header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setHeaderSolid(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Parallax mouse tracking for hero
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMousePos({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 20,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 20,
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSubmitError('');

    try {
      const { error } = await supabase
        .from('creator_applications')
        .insert({
          name: formData.name,
          contact: formData.contact,
          phone: formData.phone,
          line_id: formData.lineId,
          social_links: formData.socialLinks,
          game_type: formData.gameType,
          experience: formData.experience,
          status: 'pending',
        });

      if (error) throw error;

      if (typeof window !== 'undefined' && (window as any).fbq) {
        (window as any).fbq('track', 'Lead');
        console.log('✅ Facebook Lead 事件已觸發');
      } else {
        console.warn('⚠️ Facebook Pixel 未載入');
      }

      setSubmitSuccess(true);
      setFormData({
        name: '',
        contact: '',
        phone: '',
        lineId: '',
        socialLinks: '',
        gameType: '',
        experience: '',
      });
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitError('提交失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    
    // 電話號碼限制：只允許數字，最多 10 位
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '').slice(0, 10);
      setFormData({ ...formData, [name]: numericValue });
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const faqs = [
    {
      question: '一定要很多粉絲嗎？',
      answer:
        '不用。我們重視的是內容品質與玩法說明的清晰度，而不是粉絲數量。只要你能把遊戲規則講清楚，讓觀眾理解玩法，就符合我們的合作條件。',
    },
    {
      question: '不會剪輯可以嗎？',
      answer:
        '不可以。需要具備基本的影片剪輯能力，確保影片內容完整、流暢，並能清楚呈現遊戲玩法與規則說明。',
    },
    {
      question: '這是賭博推廣嗎？',
      answer:
        '不是。本計畫是純粹的玩法教學與體驗分享，目的是讓人理解遊戲規則與流程，不涉及任何賭博行為、投注或獲利宣稱。',
    },
    {
      question: '你們是遊戲公司嗎？',
      answer:
        '不是。我們不代表任何遊戲公司，也不從事遊戲營運或推廣。本平台是獨立的創作者媒合平台，專注於建立創作者人才庫，為未來的合作項目做準備。',
    },
    {
      question: '平台有獲利嗎？',
      answer:
        '沒有。本平台目前並無任何獲利，所有獎金都是為了鼓勵創作者參與，並建立長期合作關係。我們的目標是發掘有才華的創作者，為未來更多專案建立人才資源。',
    },
    {
      question: '獎金怎麼計算？',
      answer:
        '每在一個平台（Instagram、TikTok、YouTube）發布影片，審核通過後即可獲得 NT$1,000。發布 1 個平台得 NT$1,000、2 個平台得 NT$2,000、3 個平台得 NT$3,000，以此類推。',
    },
    {
      question: '可以只用一個平台嗎？',
      answer:
        '可以。您可以選擇只在 Instagram、TikTok 或 YouTube 其中一個平台發布，審核通過即可獲得 NT$1,000。當然，發布越多平台賺越多，每多一個平台就多 NT$1,000。',
    },
  ];

  return (
    <div
      ref={scrollRef}
      className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900"
    >
      <ScrollProgress />

      {/* Header */}
      <header
        className={`sticky top-0 z-40 transition-all duration-500 ${
          headerSolid
            ? 'bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50 shadow-lg shadow-black/20'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <img
            src="https://public.readdy.ai/ai/img_res/43e5ec71-732c-47e6-8652-53c813fd533d.png"
            alt="Logo"
            className={`transition-all duration-500 ${
              headerSolid ? 'h-7 sm:h-8' : 'h-8 sm:h-10'
            }`}
          />
          <Link
            to="/auth/login"
            className="bg-teal-500 hover:bg-teal-600 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-teal-500/30 hover:scale-105 whitespace-nowrap cursor-pointer text-xs sm:text-sm ripple-effect"
          >
            創作者登入
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        <ParticleBackground />
        {/* Glow orbs */}
        <div
          className="glow-orb absolute top-20 left-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-teal-500/20 rounded-full blur-3xl pointer-events-none"
          style={{
            transform: `translate(${mousePos.x * 0.5}px, ${mousePos.y * 0.5}px)`,
          }}
        />
        <div
          className="glow-orb absolute bottom-10 right-1/4 w-48 h-48 sm:w-80 sm:h-80 bg-purple-500/15 rounded-full blur-3xl pointer-events-none"
          style={{
            transform: `translate(${mousePos.x * -0.3}px, ${mousePos.y * -0.3}px)`,
            animationDelay: '2s',
          }}
        />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="mb-6 sm:mb-8 flex justify-center reveal">
            <img
              src="https://public.readdy.ai/ai/img_res/43e5ec71-732c-47e6-8652-53c813fd533d.png"
              alt="Logo"
              className="h-16 sm:h-24 drop-shadow-2xl"
              style={{
                transform: `translate(${mousePos.x * 0.2}px, ${mousePos.y * 0.2}px)`,
              }}
            />
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight reveal stagger-1">
            <TypingText text="創作遊戲教學影片" />
            <br className="sm:hidden" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-purple-400 to-pink-400 animated-gradient-text">
              <br className="hidden sm:block" />
              賺取豐厚獎金
            </span>
          </h1>
          <p className="text-sm sm:text-lg md:text-xl text-slate-300 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-2 reveal stagger-2">
            專注於推筒子、百家樂等牌類遊戲的教學內容創作平台
            <span className="hidden sm:inline">
              <br />每個平台發布即得 NT$1,000，最多三平台賺 NT$3,000
            </span>
          </p>
          <div className="reveal stagger-3 relative inline-block">
            <button
              onClick={() => setShowApplyForm(true)}
              className="relative bg-gradient-to-r from-teal-500 via-teal-500 to-emerald-500 hover:from-teal-600 hover:via-teal-600 hover:to-emerald-600 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all duration-300 shadow-2xl hover:shadow-teal-500/40 hover:scale-105 whitespace-nowrap cursor-pointer btn-shimmer pulse-ring ripple-effect"
            >
              立即申請成為創作者
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-slate-800/30">
        <div className="max-w-6xl mx-auto grid grid-cols-3 gap-4 sm:gap-8">
          <div className="text-center reveal stagger-1">
            <div className="text-2xl sm:text-4xl md:text-5xl font-bold text-teal-400 mb-1 sm:mb-2">
              <AnimatedCounter value={1000} prefix="NT$" />
            </div>
            <div className="text-xs sm:text-sm text-slate-400">每個平台</div>
          </div>
          <div className="text-center reveal stagger-2">
            <div className="text-2xl sm:text-4xl md:text-5xl font-bold text-purple-400 mb-1 sm:mb-2">
              <AnimatedCounter value={3000} prefix="NT$" />
            </div>
            <div className="text-xs sm:text-sm text-slate-400">三平台最高</div>
          </div>
          <div className="text-center reveal stagger-3">
            <div className="text-2xl sm:text-4xl md:text-5xl font-bold text-pink-400 mb-1 sm:mb-2">
              <AnimatedCounter value={24} suffix="hr" />
            </div>
            <div className="text-xs sm:text-sm text-slate-400">快速審核</div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-8 sm:mb-16 reveal">
            簡單三步驟<span className="hidden sm:inline">，開始賺取獎金</span>
          </h2>

          {/* Desktop Version */}
          <div className="hidden sm:grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="relative reveal stagger-1">
              <div className="card-interactive bg-slate-800/50 rounded-2xl p-8 border border-slate-700 hover:border-teal-500/50 transition-all duration-300 h-full group">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg magnetic-hover group-hover:shadow-teal-500/30 group-hover:shadow-xl transition-shadow duration-300">
                  <i className="ri-user-add-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  1. 申請成為創作者
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  填寫簡單的申請表單，告訴我們你的創作經驗與專長領域
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-teal-500 to-transparent"></div>
            </div>

            <div className="relative reveal stagger-2">
              <div className="card-interactive bg-slate-800/50 rounded-2xl p-8 border border-slate-700 hover:border-purple-500/50 transition-all duration-300 h-full group">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg magnetic-hover group-hover:shadow-purple-500/30 group-hover:shadow-xl transition-shadow duration-300">
                  <i className="ri-video-add-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  2. 製作教學影片
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  創作高品質的遊戲規則與玩法教學內容，發布到社群平台
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-purple-500 to-transparent"></div>
            </div>

            <div className="reveal stagger-3">
              <div className="card-interactive bg-slate-800/50 rounded-2xl p-8 border border-slate-700 hover:border-pink-500/50 transition-all duration-300 h-full group">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg magnetic-hover group-hover:shadow-pink-500/30 group-hover:shadow-xl transition-shadow duration-300">
                  <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  3. 獲得獎金
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  每在一個平台發布即獲得 NT$1,000，最多三平台共 NT$3,000
                </p>
              </div>
            </div>
          </div>

          {/* Mobile Version */}
          <div className="sm:hidden space-y-4">
            {[
              {
                icon: 'ri-user-add-line',
                title: '1. 申請成為創作者',
                desc: '填寫申請表單，告訴我們你的創作經驗',
                gradient: 'from-teal-500 to-teal-600',
              },
              {
                icon: 'ri-video-add-line',
                title: '2. 製作教學影片',
                desc: '創作遊戲規則教學，發布到社群平台',
                gradient: 'from-purple-500 to-purple-600',
              },
              {
                icon: 'ri-money-dollar-circle-line',
                title: '3. 獲得獎金',
                desc: '每平台發布即得 NT$1,000，最多 NT$3,000',
                gradient: 'from-pink-500 to-pink-600',
              },
            ].map((step, i) => (
              <div key={i} className={`reveal stagger-${i + 1}`}>
                <div className="card-interactive bg-slate-800/50 rounded-xl p-5 border border-slate-700">
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-12 h-12 bg-gradient-to-br ${step.gradient} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg magnetic-hover`}
                    >
                      <i className={`${step.icon} text-xl text-white`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-white mb-1.5">
                        {step.title}
                      </h3>
                      <p className="text-slate-400 text-xs leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-slate-800/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-8 sm:mb-16 reveal">
            獎金制度
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
            <div className="reveal-left stagger-1">
              <div className="card-interactive bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-slate-700 shadow-xl h-full">
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-teal-500/20 rounded-xl sm:rounded-2xl flex items-center justify-center magnetic-hover">
                    <i className="ri-checkbox-circle-line text-2xl sm:text-3xl text-teal-400"></i>
                  </div>
                  <h3 className="text-lg sm:text-2xl font-bold text-white">
                    平台獎金
                  </h3>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-teal-400 mb-3 sm:mb-4">
                  NT$1,000 / 平台
                </div>
                <p className="text-slate-400 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
                  每在一個平台發布，即可獲得 NT$1,000 獎金
                </p>
                <ul className="space-y-2 sm:space-y-3">
                  {[
                    '發布 1 個平台：NT$1,000',
                    '發布 2 個平台：NT$2,000',
                    '發布 3 個平台：NT$3,000',
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-slate-300 text-xs sm:text-sm group/item"
                    >
                      <i className="ri-check-line text-teal-400 mt-0.5 flex-shrink-0 group-hover/item:scale-125 transition-transform"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="reveal-right stagger-2">
              <div className="card-interactive bg-gradient-to-br from-purple-900/30 to-pink-900/30 rounded-xl sm:rounded-2xl p-6 sm:p-8 border border-purple-500/30 shadow-xl h-full">
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-purple-500/20 rounded-xl sm:rounded-2xl flex items-center justify-center magnetic-hover">
                    <i className="ri-rocket-line text-2xl sm:text-3xl text-purple-400"></i>
                  </div>
                  <h3 className="text-lg sm:text-2xl font-bold text-white">
                    發放條件
                  </h3>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-purple-400 mb-3 sm:mb-4">
                  簡單快速
                </div>
                <p className="text-slate-400 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
                  審核通過後，3 個工作天內發放獎金
                </p>
                <ul className="space-y-2 sm:space-y-3">
                  {[
                    '影片需完整呈現遊戲玩法與規則',
                    '不可引導穩贏不輸策略',
                    '純粹的攻略經驗分享可以',
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-slate-300 text-xs sm:text-sm group/item"
                    >
                      <i className="ri-check-line text-purple-400 mt-0.5 flex-shrink-0 group-hover/item:scale-125 transition-transform"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-8 sm:mb-16 reveal">
            內容要求
          </h2>
          <div className="reveal-scale">
            <div className="bg-slate-800/50 rounded-xl sm:rounded-2xl p-6 sm:p-10 border border-slate-700 hover:border-slate-600 transition-colors duration-500">
              <div className="grid grid-cols-1 gap-6 sm:gap-8">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-teal-400 mb-3 sm:mb-4 flex items-center gap-2">
                    <i className="ri-check-double-line text-lg sm:text-xl"></i>
                    必須包含
                  </h3>
                  <ul className="space-y-2 sm:space-y-3">
                    {[
                      '完整的遊戲規則說明',
                      '清晰的玩法演示',
                      '牌型判定與計分方式',
                      '策略技巧分享',
                    ].map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-slate-300 text-xs sm:text-sm group/req hover:translate-x-1 transition-transform duration-300"
                      >
                        <i className="ri-arrow-right-s-line text-teal-400 mt-0.5 flex-shrink-0 group-hover/req:translate-x-0.5 transition-transform"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-slate-800/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-center mb-8 sm:mb-16 reveal">
            常見問題
          </h2>
          <div className="space-y-3 sm:space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`reveal stagger-${Math.min(index + 1, 5)}`}
              >
                <div
                  className={`bg-slate-800/50 rounded-xl sm:rounded-2xl border transition-all duration-500 ${
                    expandedFaq === index
                      ? 'border-teal-500/50 shadow-lg shadow-teal-500/5'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <button
                    onClick={() =>
                      setExpandedFaq(expandedFaq === index ? null : index)
                    }
                    className="w-full px-5 sm:px-8 py-4 sm:py-6 flex items-center justify-between text-left cursor-pointer hover:bg-slate-800/30 transition-colors duration-300"
                  >
                    <span className="text-sm sm:text-base md:text-lg font-medium text-white pr-4">
                      {faq.question}
                    </span>
                    <div
                      className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                        expandedFaq === index
                          ? 'bg-teal-500/20 rotate-180'
                          : 'bg-slate-700/50'
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-xl sm:text-2xl text-teal-400"></i>
                    </div>
                  </button>
                  <div
                    className={`faq-answer ${
                      expandedFaq === index ? 'expanded' : ''
                    }`}
                  >
                    <div>
                      <div className="px-5 sm:px-8 pb-4 sm:pb-6 text-xs sm:text-sm text-slate-400 leading-relaxed border-t border-slate-700/50">
                        <div className="pt-4">{faq.answer}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-purple-500/5 to-pink-500/5"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6 reveal">
            準備好開始創作了嗎？
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-slate-300 mb-8 sm:mb-10 px-2 reveal stagger-1">
            加入我們的創作者社群，分享你的專業知識，獲得實質回報
          </p>
          <div className="reveal stagger-2 relative inline-block">
            <button
              onClick={() => setShowApplyForm(true)}
              className="relative bg-gradient-to-r from-teal-500 via-teal-500 to-emerald-500 hover:from-teal-600 hover:via-teal-600 hover:to-emerald-600 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all duration-300 shadow-2xl hover:shadow-teal-500/40 hover:scale-105 whitespace-nowrap cursor-pointer ripple-effect pulse-ring"
            >
              立即申請成為創作者
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900/50 border-t border-slate-800 py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-400 text-xs sm:text-sm">© 2025 版權所有</p>
        </div>
      </footer>

      {/* LINE@ 客服按鈕 */}
      <a
        href="https://lin.ee/pOxSsmV"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#06C755] hover:bg-[#05b34c] rounded-full flex items-center justify-center shadow-2xl hover:shadow-[#06C755]/50 transition-all duration-300 hover:scale-110 cursor-pointer group"
        aria-label="LINE 客服"
      >
        <i className="ri-customer-service-2-fill text-2xl text-white group-hover:scale-110 transition-transform"></i>
        <div className="absolute -top-12 right-0 bg-slate-800 text-white text-xs px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
          聯繫客服
          <div className="absolute bottom-0 right-6 transform translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800"></div>
        </div>
      </a>

      {/* Application Form Modal */}
      {showApplyForm && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center sm:overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowApplyForm(false);
              setSubmitSuccess(false);
              setSubmitError('');
            }
          }}
        >
          <div
            className="bg-slate-800 w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] sm:rounded-2xl sm:max-w-2xl sm:my-8 border-0 sm:border border-slate-700 shadow-2xl flex flex-col overflow-hidden"
            style={{
              animation:
                'count-up 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {/* 固定頂部標題列 */}
            <div className="flex-shrink-0 bg-slate-800 px-5 sm:px-8 pt-4 sm:pt-8 pb-3 sm:pb-0 border-b border-slate-700 sm:border-b-0">
              <div className="flex items-center justify-between mb-0 sm:mb-8">
                <h2 className="text-lg sm:text-2xl font-bold text-white">
                  申請成為創作者
                </h2>
                <button
                  onClick={() => {
                    setShowApplyForm(false);
                    setSubmitSuccess(false);
                    setSubmitError('');
                  }}
                  className="text-slate-400 hover:text-white transition-all duration-300 cursor-pointer w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-700 hover:rotate-90 -mr-1"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-5 sm:px-8 pb-6 sm:pb-8 pt-4 sm:pt-0">
              {submitSuccess ? (
                <div className="text-center py-8 sm:py-12">
                  <div
                    className="w-16 h-16 sm:w-20 sm:h-20 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6"
                    style={{
                      animation:
                        'count-up 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  >
                    <i className="ri-check-line text-3xl sm:text-4xl text-teal-400"></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                    申請已提交！
                  </h3>
                  <p className="text-sm sm:text-base text-slate-400 mb-6 sm:mb-8 px-2">
                    我們將在 24 小時內審核您的申請，並透過電子郵件通知您結果
                  </p>
                  <button
                    onClick={() => {
                      setShowApplyForm(false);
                      setSubmitSuccess(false);
                      setFormData({
                        name: '',
                        contact: '',
                        phone: '',
                        lineId: '',
                        socialLinks: '',
                        gameType: '',
                        experience: '',
                      });
                    }}
                    className="bg-teal-500 hover:bg-teal-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base hover:scale-105"
                  >
                    關閉
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  {submitError && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 sm:p-4">
                      <p className="text-red-400 text-xs sm:text-sm">{submitError}</p>
                    </div>
                  )}

                  {/* 姓名 */}
                  <div>
                    <label className="block text-slate-300 text-xs sm:text-sm font-medium mb-2">
                      姓名 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="請輸入您的姓名或暱稱"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-all duration-300 text-sm sm:text-base"
                      autoComplete="name"
                    />
                  </div>

                  {/* 電話號碼 */}
                  <div>
                    <label className="block text-slate-300 text-xs sm:text-sm font-medium mb-2">
                      電話號碼 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="09xxxxxxxx"
                      maxLength={10}
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-all duration-300 text-sm sm:text-base"
                      autoComplete="tel"
                    />
                    <p className="text-slate-500 text-[10px] sm:text-xs mt-1.5">
                      {formData.phone.length}/10 位數字
                    </p>
                  </div>

                  {/* LINE ID */}
                  <div>
                    <label className="block text-slate-300 text-xs sm:text-sm font-medium mb-2">
                      LINE ID <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="lineId"
                      value={formData.lineId}
                      onChange={handleInputChange}
                      required
                      placeholder="請輸入您的 LINE ID"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-all duration-300 text-sm sm:text-base"
                    />
                  </div>

                  {/* 電子郵件 */}
                  <div>
                    <label className="block text-slate-300 text-xs sm:text-sm font-medium mb-2">
                      電子郵件 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="contact"
                      value={formData.contact}
                      onChange={handleInputChange}
                      required
                      placeholder="example@email.com"
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-all duration-300 text-sm sm:text-base"
                      autoComplete="email"
                      inputMode="email"
                    />
                  </div>

                  {/* 社群媒體帳號 */}
                  <div>
                    <label className="block text-slate-300 text-xs sm:text-sm font-medium mb-2">
                      社群媒體帳號 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="socialLinks"
                      value={formData.socialLinks}
                      onChange={handleInputChange}
                      required
                      placeholder="請提供您的 YouTube、Facebook、Instagram 等社群平台連結或參考作品"
                      rows={3}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-all duration-300 resize-none text-sm sm:text-base"
                    />
                  </div>

                  {/* 遊戲類型 */}
                  <div>
                    <label className="block text-slate-300 text-xs sm:text-sm font-medium mb-2">
                      擅長的遊戲類型 <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="gameType"
                      value={formData.gameType}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-white focus:outline-none focus:border-teal-500 transition-all duration-300 appearance-none cursor-pointer text-sm sm:text-base"
                      style={{
                        backgroundImage:
                          'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%2394a3b8\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 12px center',
                      }}
                    >
                      <option value="">請選擇遊戲類型</option>
                      <option value="推筒子">推筒子</option>
                      <option value="百家樂">百家樂</option>
                      <option value="其他牌類遊戲">其他牌類遊戲</option>
                    </select>
                  </div>

                  {/* 創作經驗 */}
                  <div>
                    <label className="block text-slate-300 text-xs sm:text-sm font-medium mb-2">
                      創作經驗 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="experience"
                      value={formData.experience}
                      onChange={handleInputChange}
                      required
                      placeholder="請簡述您的創作經驗、頻道規模、觀眾群等"
                      rows={4}
                      maxLength={500}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 sm:px-4 py-2.5 sm:py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-all duration-300 resize-none text-sm sm:text-base"
                    />
                    <p className="text-slate-500 text-[10px] sm:text-xs mt-1.5">
                      {formData.experience.length}/500 字元
                    </p>
                  </div>

                  {/* 提交按鈕 */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white py-3 sm:py-3.5 rounded-lg font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-sm sm:text-base hover:scale-[1.02] active:scale-[0.98]"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : (
                      '提交申請'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
